import { useEffect, useMemo, useState } from 'react';
import { fetchAllParticipantRanks, ParticipantRankRow } from '../api/contests';

export type ParticipantRanksMap = Map<number, ParticipantRankRow[]>;

/**
 * Loads participant ranks once and exposes a Map participant_id -> rank types.
 * 
 * Notes:
 * - We intentionally keep the value as an array (not unique) because different contests may award same rank.
 *   The rendering component will dedupe/limit.
 */
export function useParticipantRanks() {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);
  const [rows, setRows] = useState<ParticipantRankRow[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchAllParticipantRanks();
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const ranksByParticipantId: ParticipantRanksMap = useMemo(() => {
    const m = new Map<number, ParticipantRankRow[]>();
    rows.forEach((row) => {
      const arr = m.get(row.participant_id) ?? [];
      arr.push(row);
      m.set(row.participant_id, arr);
    });
    return m;
  }, [rows]);

  return { ranksByParticipantId, loading, error };
}
